import { Injectable, inject } from '@angular/core';
import { OpenAIBaseService } from './open-ai-base.service';
import { SupabaseService } from './supabase.service';

/**
 * Company metadata structure stored in database
 */
export interface CompanyMetadata {
  company_name: string;
  industries: string[];
  company_size: string | null;  // startup, small, medium, large, enterprise
  company_stage: string | null; // bootstrapped, seed, series-a, series-b, series-c-plus, public, acquired
  remote_culture: string | null; // remote-first, hybrid, office-first, distributed
  tech_stack: string[];
  work_style: string[];
  benefits: string[];
}

/**
 * Service for managing comprehensive company metadata
 * Used for advanced job matching based on company attributes
 */
@Injectable({
  providedIn: 'root'
})
export class CompanyMetadataService extends OpenAIBaseService {
  private supabaseService = inject(SupabaseService);
  
  // In-memory cache for current session (backed by database for persistence)
  private metadataCache: Map<string, CompanyMetadata> = new Map();

  constructor() {
    super();
  }

  /**
   * Get metadata for multiple companies in batch
   * Returns a map of company name -> metadata
   */
  async batchGetMetadata(companyNames: string[]): Promise<Map<string, CompanyMetadata>> {
    const results = new Map<string, CompanyMetadata>();
    const normalizedNames = companyNames.map(n => n.toLowerCase().trim());
    
    // Check memory cache first
    const needsLookup: string[] = [];
    for (const name of normalizedNames) {
      if (this.metadataCache.has(name)) {
        results.set(name, this.metadataCache.get(name)!);
      } else {
        needsLookup.push(name);
      }
    }

    if (needsLookup.length === 0) {
      return results;
    }

    // Load from database
    const dbResults = await this.loadFromDatabase(needsLookup);
    for (const [name, metadata] of dbResults) {
      results.set(name, metadata);
      this.metadataCache.set(name, metadata);
    }

    return results;
  }

  /**
   * Check if a company matches a specific criterion
   */
  matchesCriterion(
    metadata: CompanyMetadata | undefined,
    category: string,
    signalText: string
  ): { matches: boolean; reason?: string } {
    if (!metadata) {
      return { matches: false, reason: 'No metadata available' };
    }

    // Include 2-letter keywords like "ai" and "hr"
    const keywords = signalText.toLowerCase().split(/\s+/).filter(k => k.length > 1);

    switch (category) {
      case 'company_type':
      case 'industry':
        return this.matchesIndustry(metadata, keywords);
      
      case 'company_size':
        return this.matchesSize(metadata, keywords);
      
      case 'company_stage':
        return this.matchesStage(metadata, keywords);
      
      case 'remote_culture':
        return this.matchesRemoteCulture(metadata, keywords);
      
      case 'tech_stack':
      case 'tools':
        return this.matchesTechStack(metadata, keywords);
      
      case 'work_style':
      case 'culture':
        return this.matchesWorkStyle(metadata, keywords);
      
      case 'benefits':
      case 'perks':
        return this.matchesBenefits(metadata, keywords);
      
      default:
        return { matches: false, reason: `Unknown category: ${category}` };
    }
  }

  /**
   * Check if company matches industry keywords
   */
  private matchesIndustry(metadata: CompanyMetadata, keywords: string[]): { matches: boolean; reason?: string } {
    // Map user keywords to our standardized industry tags
    const keywordToIndustry: { [key: string]: string[] } = {
      'crypto': ['crypto'],
      'cryptocurrency': ['crypto'],
      'blockchain': ['crypto'],
      'web3': ['crypto'],
      'nft': ['crypto'],
      'defi': ['crypto'],
      'staking': ['crypto'],
      'bitcoin': ['crypto'],
      'ethereum': ['crypto'],
      'solana': ['crypto'],
      'tezos': ['crypto'],
      'decentralized': ['crypto'],
      'fintech': ['fintech'],
      'financial': ['fintech'],
      'banking': ['fintech'],
      'payments': ['fintech'],
      'lending': ['fintech'],
      'insurance': ['fintech'],
      'trading': ['fintech'],
      'investment': ['fintech'],
      'ai': ['ai'],
      'artificial': ['ai'],
      'intelligence': ['ai'],
      'machine': ['ai'],
      'learning': ['ai'],
      'llm': ['ai'],
      'gpt': ['ai'],
      'neural': ['ai'],
      'saas': ['saas'],
      'software': ['saas'],
      'ecommerce': ['ecommerce'],
      'retail': ['ecommerce'],
      'marketplace': ['ecommerce'],
      'gaming': ['gaming'],
      'game': ['gaming'],
      'games': ['gaming'],
      'healthcare': ['healthcare'],
      'health': ['healthcare'],
      'medical': ['healthcare'],
      'social': ['social'],
      'media': ['social'],
      'networking': ['social'],
      'enterprise': ['enterprise'],
      'b2b': ['enterprise'],
    };

    for (const keyword of keywords) {
      const mappedIndustries = keywordToIndustry[keyword];
      if (mappedIndustries) {
        for (const industry of mappedIndustries) {
          if (metadata.industries.includes(industry)) {
            return { matches: true, reason: `Industry: ${industry}` };
          }
        }
      }
    }

    return { matches: false, reason: `Industries [${metadata.industries.join(', ')}] don't match` };
  }

  /**
   * Check if company matches size preference
   */
  private matchesSize(metadata: CompanyMetadata, keywords: string[]): { matches: boolean; reason?: string } {
    if (!metadata.company_size) {
      return { matches: false, reason: 'Company size unknown' };
    }

    const sizeKeywords: { [key: string]: string[] } = {
      'startup': ['startup'],
      'small': ['small', 'startup'],
      'medium': ['medium'],
      'large': ['large'],
      'enterprise': ['enterprise', 'large'],
      'big': ['large', 'enterprise'],
      'tiny': ['startup'],
    };

    for (const keyword of keywords) {
      const validSizes = sizeKeywords[keyword];
      if (validSizes && validSizes.includes(metadata.company_size)) {
        return { matches: true, reason: `Size: ${metadata.company_size}` };
      }
    }

    return { matches: false, reason: `Size ${metadata.company_size} doesn't match` };
  }

  /**
   * Check if company matches stage preference
   */
  private matchesStage(metadata: CompanyMetadata, keywords: string[]): { matches: boolean; reason?: string } {
    if (!metadata.company_stage) {
      return { matches: false, reason: 'Company stage unknown' };
    }

    const stageKeywords: { [key: string]: string[] } = {
      'bootstrapped': ['bootstrapped'],
      'bootstrap': ['bootstrapped'],
      'seed': ['seed'],
      'early': ['seed', 'series-a'],
      'series': ['series-a', 'series-b', 'series-c-plus'],
      'public': ['public'],
      'ipo': ['public'],
      'acquired': ['acquired'],
      'late': ['series-c-plus', 'public'],
      'growth': ['series-b', 'series-c-plus'],
    };

    for (const keyword of keywords) {
      const validStages = stageKeywords[keyword];
      if (validStages && validStages.includes(metadata.company_stage)) {
        return { matches: true, reason: `Stage: ${metadata.company_stage}` };
      }
    }

    return { matches: false, reason: `Stage ${metadata.company_stage} doesn't match` };
  }

  /**
   * Check if company matches remote culture preference
   */
  private matchesRemoteCulture(metadata: CompanyMetadata, keywords: string[]): { matches: boolean; reason?: string } {
    if (!metadata.remote_culture) {
      return { matches: false, reason: 'Remote culture unknown' };
    }

    const cultureKeywords: { [key: string]: string[] } = {
      'remote': ['remote-first', 'distributed'],
      'remote-first': ['remote-first'],
      'distributed': ['distributed', 'remote-first'],
      'hybrid': ['hybrid'],
      'flexible': ['hybrid', 'remote-first'],
      'office': ['office-first'],
      'onsite': ['office-first'],
      'in-person': ['office-first'],
    };

    for (const keyword of keywords) {
      const validCultures = cultureKeywords[keyword];
      if (validCultures && validCultures.includes(metadata.remote_culture)) {
        return { matches: true, reason: `Remote culture: ${metadata.remote_culture}` };
      }
    }

    return { matches: false, reason: `Remote culture ${metadata.remote_culture} doesn't match` };
  }

  /**
   * Check if company uses specific tools
   */
  private matchesTechStack(metadata: CompanyMetadata, keywords: string[]): { matches: boolean; reason?: string } {
    if (!metadata.tech_stack || metadata.tech_stack.length === 0) {
      return { matches: false, reason: 'Tech stack unknown' };
    }

    for (const keyword of keywords) {
      if (metadata.tech_stack.includes(keyword)) {
        return { matches: true, reason: `Uses: ${keyword}` };
      }
    }

    return { matches: false, reason: `Tech stack doesn't include requested tools` };
  }

  /**
   * Check if company matches work style preference
   */
  private matchesWorkStyle(metadata: CompanyMetadata, keywords: string[]): { matches: boolean; reason?: string } {
    if (!metadata.work_style || metadata.work_style.length === 0) {
      return { matches: false, reason: 'Work style unknown' };
    }

    const styleKeywords: { [key: string]: string[] } = {
      'fast': ['fast-paced'],
      'fast-paced': ['fast-paced'],
      'balance': ['work-life-balance'],
      'work-life-balance': ['work-life-balance'],
      'async': ['async-first'],
      'async-first': ['async-first'],
      'agile': ['agile'],
      'startup': ['startup-mentality', 'fast-paced'],
      'collaborative': ['collaborative'],
      'innovative': ['innovative'],
      'data-driven': ['data-driven'],
    };

    for (const keyword of keywords) {
      // Direct match
      if (metadata.work_style.includes(keyword)) {
        return { matches: true, reason: `Work style: ${keyword}` };
      }
      // Keyword mapping
      const validStyles = styleKeywords[keyword];
      if (validStyles) {
        for (const style of validStyles) {
          if (metadata.work_style.includes(style)) {
            return { matches: true, reason: `Work style: ${style}` };
          }
        }
      }
    }

    return { matches: false, reason: `Work style doesn't match preferences` };
  }

  /**
   * Check if company offers specific benefits
   */
  private matchesBenefits(metadata: CompanyMetadata, keywords: string[]): { matches: boolean; reason?: string } {
    if (!metadata.benefits || metadata.benefits.length === 0) {
      return { matches: false, reason: 'Benefits unknown' };
    }

    const benefitKeywords: { [key: string]: string[] } = {
      'equity': ['equity'],
      'stock': ['equity'],
      'options': ['equity'],
      'pto': ['unlimited-pto'],
      'unlimited': ['unlimited-pto'],
      '4-day': ['4-day-week'],
      'four-day': ['4-day-week'],
      'health': ['health-insurance'],
      'insurance': ['health-insurance'],
      'learning': ['learning-budget'],
      'education': ['learning-budget'],
      'wellness': ['wellness-stipend'],
      'remote-stipend': ['remote-stipend'],
      'home-office': ['remote-stipend'],
      '401k': ['401k'],
      'retirement': ['401k'],
      'parental': ['parental-leave'],
      'maternity': ['parental-leave'],
      'paternity': ['parental-leave'],
    };

    for (const keyword of keywords) {
      // Direct match
      if (metadata.benefits.includes(keyword)) {
        return { matches: true, reason: `Offers: ${keyword}` };
      }
      // Keyword mapping
      const validBenefits = benefitKeywords[keyword];
      if (validBenefits) {
        for (const benefit of validBenefits) {
          if (metadata.benefits.includes(benefit)) {
            return { matches: true, reason: `Offers: ${benefit}` };
          }
        }
      }
    }

    return { matches: false, reason: `Benefits don't match preferences` };
  }

  /**
   * Load metadata from database
   */
  private async loadFromDatabase(companyNames: string[]): Promise<Map<string, CompanyMetadata>> {
    const results = new Map<string, CompanyMetadata>();
    
    try {
      const { data, error } = await this.supabaseService
        .getClient()
        .from('company_industries')
        .select('company_name, industries, company_size, company_stage, remote_culture, tech_stack, work_style, benefits')
        .in('company_name', companyNames);

      if (!error && data) {
        for (const row of data) {
          const metadata: CompanyMetadata = {
            company_name: row.company_name,
            industries: row.industries || [],
            company_size: row.company_size || null,
            company_stage: row.company_stage || null,
            remote_culture: row.remote_culture || null,
            tech_stack: row.tech_stack || [],
            work_style: row.work_style || [],
            benefits: row.benefits || []
          };
          results.set(row.company_name.toLowerCase(), metadata);
          this.metadataCache.set(row.company_name.toLowerCase(), metadata);
        }
      }
    } catch (err) {
      console.error('Error loading company metadata from database:', err);
    }

    return results;
  }

  /**
   * Clear the cache (useful for testing)
   */
  clearCache(): void {
    this.metadataCache.clear();
  }

  /**
   * Get all cached metadata (for debugging)
   */
  getCachedMetadata(): Map<string, CompanyMetadata> {
    return new Map(this.metadataCache);
  }
}
